// Copyright (c) 2021 WiseTime. All rights reserved.

import { forkJoin, iif, Observable, of, throwError } from "rxjs"
import { Action } from "redux"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { create } from "@bufbuild/protobuf"
import { executorNoRefreshSuccessCallback, Init, membershipService, userService } from "../../../app/rpc"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { User, WhoAmIRequestSchema } from "../../../generated/wisetime/account/user/v4/user_pb"
import { GetMembershipRequestSchema, Membership } from "../../../generated/legebill/site/membership/v4/membership_pb"
import { from } from "@wisetime-io/connect-rx"

type WhoAmIRpc = (request: Init<typeof WhoAmIRequestSchema>) => Observable<User>
type GetMembershipRpc = (request: Init<typeof GetMembershipRequestSchema>) => Observable<Membership>

type ErrorNoSiteMemberships = "NO_SITE_MEMBERSHIPS"

/**
 * Fetches the user details from the WhoAmI endpoint plus user memberships from the GetMembership endpoint, and updates
 * the store.
 *
 * If the request is made to enforce user login then the retry action will attempt a token exchange before retrying
 * the source call, if this call fails then the user is redirected to the login page.
 */
export const fetchUserDetails = (
  whoAmIRpc: WhoAmIRpc,
  getMembershipRpc: GetMembershipRpc,
) => (action$: Observable<Action>): Observable<Action> =>
  action$.pipe(
    filter(Actions.fetchUserDetails.match),
    mergeMap(() =>
      whoAmIRpc({})
        .pipe(
          mergeMap(ensureAtLeastOneMembership),
          mergeMap(user => fetchMembershipDetails(user, getMembershipRpc)
            .pipe(
              map(memberships => ({
                user,
                memberships,
              }))
            )
          ),
          map(Slice.setUserDetails),
          catchError(handleError),
        )
    )
  )

const whoAmIRpc = (request: Init<typeof WhoAmIRequestSchema>) =>
  executorNoRefreshSuccessCallback(() => userService.whoAmI(create(WhoAmIRequestSchema, request)))

const getMembershipRpc = (request: Init<typeof GetMembershipRequestSchema>): Observable<Membership> =>
  from(() => membershipService.getMembership(create(GetMembershipRequestSchema, request)))

const ensureAtLeastOneMembership = (user: User) =>
  iif(
    () => user.teamSites.length > 0,
    of(user),
    throwError(() => "NO_SITE_MEMBERSHIPS")
  )

const fetchMembershipDetails = (user: User, getMembershipRpc: GetMembershipRpc): Observable<Membership[]> =>
  forkJoin(
    user.teamSites
      .map(site => {
        return getMembershipRpc({ siteId: site.id ?? "", userId: user.id })
      })
  )

const errorNoMemberships = (e: unknown): e is ErrorNoSiteMemberships => {
  if (!e) {
    return false
  }

  const err = e as ErrorNoSiteMemberships
  return err === "NO_SITE_MEMBERSHIPS"
}

const handleError = (e: unknown): Observable<Action> => {
  if (errorNoMemberships(e)) {
    return of(Actions.redirectToAddTeam())
  }
  // token exchange and fetching of user (and/or membership) details failures are already handled
  return of(Actions.fetchUserDetailsFailed())
}

export default fetchUserDetails(whoAmIRpc, getMembershipRpc)
